import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";

/**
 * Tema da interface.
 *
 * O padrão é claro. Escuro é uma escolha explícita, gravada em localStorage,
 * para não surpreender quem abre o calendário operacional de dia.
 */

export type Theme = "light" | "dark";

const STORAGE_KEY = "dream-theme";

export function parseTheme(value: string | null | undefined): Theme {
  return value === "dark" ? "dark" : "light";
}

export function readStoredTheme(): Theme {
  try {
    return parseTheme(localStorage.getItem(STORAGE_KEY));
  } catch {
    return "light";
  }
}

function faviconHref(theme: Theme): string {
  return theme === "dark"
    ? "/brand/dream-calendar-mark-on-dark.png"
    : "/brand/dream-calendar-mark-on-light.png";
}

function iconLinks(): HTMLLinkElement[] {
  return [
    ...document.querySelectorAll<HTMLLinkElement>(
      "link[rel='icon'][type='image/png'], link[rel='apple-touch-icon']",
    ),
  ];
}

function applyFavicon(theme: Theme): void {
  const href = faviconHref(theme);
  for (const link of iconLinks()) {
    if (link.dataset.clientBrand === "1") continue;
    link.href = href;
  }
}

function ensureAppleTouchIcon(): HTMLLinkElement {
  const existing = document.querySelector<HTMLLinkElement>("link[rel='apple-touch-icon']");
  if (existing !== null) return existing;
  const created = document.createElement("link");
  created.rel = "apple-touch-icon";
  document.head.append(created);
  return created;
}

/** Favicon do `/@handle`: a logomarca do cliente, se existir. */
export function setClientBrandFavicon(href: string | null): void {
  const apple = ensureAppleTouchIcon();
  const links = iconLinks();
  if (href === null || href === "") {
    for (const link of links) {
      delete link.dataset.clientBrand;
    }
    applyFavicon(readStoredTheme());
    return;
  }
  for (const link of links) {
    link.dataset.clientBrand = "1";
    link.href = href;
  }
  apple.dataset.clientBrand = "1";
  apple.href = href;
}

export function applyTheme(theme: Theme): void {
  document.documentElement.setAttribute("data-theme", theme);
  document.documentElement.style.colorScheme = theme;
  applyFavicon(theme);
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Storage pode estar bloqueado; o atributo no <html> já vale para a sessão.
  }
}

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    applyTheme(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((current) => {
      const next: Theme = current === "dark" ? "light" : "dark";
      applyTheme(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const value = useContext(ThemeContext);
  if (value === null) {
    throw new Error("useTheme precisa estar dentro de ThemeProvider");
  }
  return value;
}
